'use client';

import { Layout_WithUser } from '@components/WithUser/WithUser';
import { useUser } from '@context/user-context';
import { useCheckout } from '@hooks/billing-subscription';
import { useCelloReferralEligibility } from '@hooks/cello/use-cello-referral-eligibility';
import { createChildLogger } from '@modules/logger';
import { useRouter } from 'next/router';
import type { NextPageWithLayout } from 'pages/_app';
import { useEffect, useState } from 'react';
import { showNotification } from 'ui-lib/mantine/notifications';

const logger = createChildLogger({ module: 'pages/onboarding/upgrade-flow-selection' });

const PREMIUM_UPGRADE_SESSION_KEY = 'premiumUpgradeSession';

// Changing these types will require changes in webflow -
type WebFlowIntervalQuery = 'month' | 'year';
type WebFlowPlanQuery = 'premium' | 'business';

function getSelectedPlanFromQuery(
  query: string | string[] | undefined,
): WebFlowPlanQuery | undefined {
  if (query === 'premium') return 'premium';
  if (query === 'business') return 'business';
  return;
}

function getSelectedIntervalFromQuery(query: string | string[] | undefined): WebFlowIntervalQuery {
  if (query === 'month') return 'month';
  if (query === 'year') return 'year';
  return 'year';
}

const UpgradeFlowSelectionPage: NextPageWithLayout = () => {
  const { user, loading } = useUser();
  const { replace, isReady, query } = useRouter();
  const { createUpgradeSession } = usePremiumUpgradeFlow();

  useEffect(() => {
    if (loading || !isReady) return;

    // Check if window is available
    if (typeof window !== 'undefined') {
      const isMobile = window.matchMedia('(max-width: 700px)');
      if (isMobile.matches) {
        const queryParams = new URLSearchParams(query as Record<string, string>).toString();
        replace(`/onboarding/personal-details?${queryParams}`);
        return;
      }
    }

    const selectedPlan = getSelectedPlanFromQuery(query.plan);

    // Business to business v2 upgrade flow
    if (selectedPlan === 'business') {
      replace('/onboarding/business/v2/signup');
      return;
    }

    const interval = getSelectedIntervalFromQuery(query.interval);
    createUpgradeSession({ interval });

    // If logged in
    if (user) {
      const isTeamAdmin = !!user.bbOrgId;
      const currentWorkspace = user.bbOrgId ?? user.userId;

      // TODO: Future optimisation, hoise home-page logic, this is not DRY
      // If user is logged in, we want to show them a dialog on their home page
      if (isTeamAdmin) {
        replace({
          pathname: `/${currentWorkspace}/home`,
        });
      } else {
        replace({
          pathname: `/${currentWorkspace}/cards/personal`,
        });
      }
    } else {
      // If the user is logged out, we need to send them to the onboarding flow.
      replace('/onboarding/customize-card');
      return;
    }
  }, [loading, user, isReady, createUpgradeSession, replace, query]);

  return <></>;
};

UpgradeFlowSelectionPage.getLayout = (page) => {
  return <Layout_WithUser redirect={false}>{page}</Layout_WithUser>;
};

export default UpgradeFlowSelectionPage;

const UPGRADE_SUCCESS_QUERY = 'upgradeSuccess';

export const usePremiumUpgradeFlow = (): {
  isInUpgradeSession: boolean;
  createUpgradeSession: (args: { interval: WebFlowIntervalQuery }) => void;
  endUpgradeSession: () => void;
  /**
   * Sends the user to Stripe for the plan their upgrade session selected. Resolves to
   * whether a checkout actually started, so a caller mid-flow can continue to its own
   * destination instead of leaving the user on a screen that has nowhere left to go.
   */
  startUpgradeCheckout: (args: {
    userId: string;
    successUrl?: string;
    cancelUrl?: string;
  }) => Promise<boolean>;
} => {
  const router = useRouter();
  const { startCheckout } = useCheckout();
  const { resolveReferralCheckoutArgs } = useCelloReferralEligibility();
  const [isInUpgradeSession, setIsInUpgradeSession] = useState(
    typeof window !== 'undefined' &&
      Boolean(window.sessionStorage.getItem(PREMIUM_UPGRADE_SESSION_KEY)),
  );

  const hasCompletedFlow = router.query[UPGRADE_SUCCESS_QUERY] === 'true';

  // biome-ignore lint/correctness/useExhaustiveDependencies: historic
  useEffect(() => {
    if (hasCompletedFlow) {
      endUpgradeSession();
    }
  }, [hasCompletedFlow]);

  const createUpgradeSession = ({ interval }: { interval: WebFlowIntervalQuery }) => {
    setIsInUpgradeSession(true);
    window?.sessionStorage.setItem(
      PREMIUM_UPGRADE_SESSION_KEY,
      JSON.stringify({
        interval,
      }),
    );
  };

  const endUpgradeSession = () => {
    setIsInUpgradeSession(false);
    window?.sessionStorage.removeItem(PREMIUM_UPGRADE_SESSION_KEY);
  };

  const startUpgradeCheckout = async ({
    userId,
    successUrl,
    cancelUrl,
  }: {
    userId: string;
    successUrl?: string;
    cancelUrl?: string;
  }): Promise<boolean> => {
    const session = window?.sessionStorage.getItem(PREMIUM_UPGRADE_SESSION_KEY);
    const interval = session ? JSON.parse(session).interval : 'year';

    if (!session || !interval) {
      return false;
    }

    window.sessionStorage.setItem(
      PREMIUM_UPGRADE_SESSION_KEY,
      JSON.stringify({
        interval,
      }),
    );

    // This sells the same INDIVIDUAL plan the referral paywall does, so a referred
    // user buying here must be charged the same discounted price and carry the same
    // subscription metadata — otherwise the upgrade session silently costs them the
    // discount and the referrer the conversion (ACT-2224).
    const referral = await resolveReferralCheckoutArgs();

    // Awaited so a failure can't pass for a started checkout. `startCheckout` throws
    // on a price the catalog can't sell and on a rejected session mutation; floating,
    // it raised an unhandled rejection and left the user on a button that did nothing.
    // The upgrade session is deliberately left intact so they can simply try again.
    try {
      await startCheckout({
        workspaceId: userId,
        interval: interval,
        type: 'INDIVIDUAL',
        successUrl: `${successUrl || window.location.href}?${UPGRADE_SUCCESS_QUERY}=true`,
        cancelUrl,
        ...referral,
      });
    } catch (error) {
      logger.error('Unable to start the premium upgrade checkout session.', { error });

      showNotification({
        tone: 'critical',
        notificationContent: { content: 'Sorry, something went wrong. Try again shortly.' },
      });

      return false;
    }

    return true;
  };

  return {
    isInUpgradeSession,
    createUpgradeSession,
    endUpgradeSession,
    startUpgradeCheckout,
  };
};
